import { Card, Flex, Table, type TablePaginationConfig, Typography } from "antd"
import type { FilterValue, SorterResult } from "antd/es/table/interface"
import { type BaseOptionType } from "antd/lib/select"
import React, { type ReactNode, useCallback, useMemo } from "react"
import { type TFunction } from "i18next"
import { useTranslation } from "react-i18next"

import { EntityTable } from "../../../../../../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../../../../../components/EntityTable/surfaces.ts"
import { useMeasuredTableHeight } from "../../../../../../../hooks/use-measured-table-height.ts"
import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import { getTableOrderingFromSorter } from "../../../../../../../utils/table"
import type {
  ProjectCommission,
  ProjectCommissionsData,
  ProjectCommissionsSummaryKeys
} from "../../../../../redux/project-commissions/project-commissions.types.ts"
import { ProjectViewCommissionsNewRow } from "./ProjectViewCommissionsNewRow.tsx"
import { getColumns, settledVsCommitted } from "./columns.tsx"

const { Text } = Typography

const getSummaryCells = (
  editAllowed?: boolean
): Array<{
  index: number
  hidden?: boolean
  keys?: [
    ProjectCommissionsSummaryKeys | null,
    (ProjectCommissionsSummaryKeys | null)?
  ]
  colSpan?: number
  align?: "center"
  render?: (
    summary: ProjectCommissionsData["summary"],
    t: TFunction
  ) => ReactNode
}> => [
  {
    index: 2,
    keys: ["office_lead_cost_plan"],
    align: "center"
  },
  { index: 3 },
  {
    index: 4,
    keys: ["referral_lead_cost_plan"],
    align: "center"
  },
  { index: 5 },
  {
    index: 6,
    keys: ["others_lead_cost_plan"],
    align: "center"
  },
  {
    index: 7,
    keys: ["total_lead_cost_plan"],
    align: "center"
  },
  { index: 8 },
  {
    index: 9,
    align: "center",
    render: (summary, t) =>
      settledVsCommitted(
        t,
        summary?.sales_commission_settled,
        summary?.sales_commission_committed,
        summary?.sales_commission_plan
      )
  },
  { index: 10, hidden: !editAllowed, colSpan: 2 }
]

interface RowProps extends React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLTableRowElement>,
  HTMLTableRowElement
> {
  record: ProjectCommission
  children?: ReactNode[]
}

interface ProjectViewCommissionsListProps {
  commissions: ProjectCommission[]
  summary: ProjectCommissionsData["summary"]
  fetching: boolean
  page: number
  count: number
  pageSize: number
  editableRow?: string
  editAllowed?: boolean
  options?: Record<string, BaseOptionType[]>
  onOrderingChange: (ordering: string | null) => void
  onSelectRow: (
    record: ProjectCommission,
    action: string
  ) => Promise<void> | void
  onPageChange: (page: number) => void
}

export function ProjectViewCommissionsList({
  commissions,
  summary,
  page,
  pageSize,
  count,
  fetching,
  editableRow = "",
  editAllowed,
  options = {},
  onOrderingChange,
  onSelectRow,
  onPageChange
}: ProjectViewCommissionsListProps) {
  const isLocked = !!editableRow
  const { t } = useTranslation()

  const columns = useMemo(
    () => getColumns({ editAllowed, onSelectRow, isLocked, t }),
    [onSelectRow, editAllowed, isLocked, t]
  )

  const summaryCells = useMemo(
    () => getSummaryCells(editAllowed).filter(({ hidden }) => !hidden),
    [editAllowed]
  )

  // The section paginates rather than scrolling on, so it wires the two height
  // hooks itself instead of taking `useScrolledTable`, which exists to add an
  // infinite-scroll listener this list has nothing to feed. `reserve` hands back
  // the pinned Total row, which sits below the body once the body has a height.
  const { tableRef, tableHeight } = useMeasuredTableHeight()

  const tableSettings = useMemo(
    () => ({
      scroll: {
        x: "max-content",
        // Without a `y` AntD renders no `.ant-table-body` at all, and a row's
        // actions menu has no viewport to be clipped in — it stays on
        // `document.body` and rides over the sticky section tabs on a page
        // scroll.
        y: tableHeight,
        scrollToFirstRowOnChange: true
      },
      pagination: {
        disabled: isLocked,
        total: count,
        current: page,
        pageSize,
        showSizeChanger: false
      }
    }),
    [count, page, pageSize, isLocked, tableHeight]
  )

  const onChangeTable = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter:
        | SorterResult<ProjectCommission>
        | SorterResult<ProjectCommission>[]
    ) => {
      const ordering = getTableOrderingFromSorter(sorter)

      onPageChange(pagination.current || 1)
      onOrderingChange(ordering)
    },
    [onPageChange, onOrderingChange]
  )

  const RowComponent = useCallback(
    ({ record, ...props }: RowProps) => {
      if (editableRow && record?.id === editableRow) {
        return (
          <ProjectViewCommissionsNewRow
            onSelectRow={onSelectRow}
            record={record}
            {...props}
          />
        )
      }

      return <tr {...props}>{props?.children}</tr>
    },
    [editableRow, onSelectRow]
  )

  return (
    <Card classNames={{ body: "p-0" }} ref={tableRef}>
      <EntityTable<ProjectCommission>
        surface={TABLE_SURFACES.projectCommissions}
        selectionMode="on-demand"
        tab={DEFAULT_TAB}
        lockColumnLayout
        rowKey="id"
        size="small"
        className="[&_th]:bg-primary [&_th]:rounded-none [&_th]:text-white"
        columns={columns}
        loading={fetching}
        dataSource={commissions}
        scroll={tableSettings.scroll}
        onChange={onChangeTable}
        // pagination={tableSettings.pagination}
        withOutPagination
        pagination={false}
        onRow={(record) => ({
          record,
          onDoubleClick: () => !isLocked && onSelectRow(record, "edit_row")
        })}
        components={{
          body: editAllowed && commissions?.length ? { row: RowComponent } : {}
        }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row className="bg-gray-100 font-semibold">
              <Table.Summary.Cell index={0} colSpan={2}>
                Total
              </Table.Summary.Cell>
              {summaryCells?.map(({ index, keys, colSpan, align, render }) => (
                <Table.Summary.Cell
                  key={index}
                  index={index}
                  colSpan={colSpan}
                  align={align}
                >
                  {render?.(summary, t)}
                  {keys && (
                    <Flex vertical>
                      {keys && (
                        <Flex vertical>
                          {keys[0] != null && (
                            <Text>{budgetFormat(summary[keys[0]])}</Text>
                          )}
                          {keys[1] != null && (
                            <Text type="secondary">
                              / {budgetFormat(summary[keys[1]])}
                            </Text>
                          )}
                        </Flex>
                      )}
                    </Flex>
                  )}
                </Table.Summary.Cell>
              ))}
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </Card>
  )
}
